import DOMPurify from 'dompurify';

/**
 * Safely constructs a URL from a host parameter to prevent DOM-based XSS attacks.
 * 
 * This function protects against Cross-Site Scripting (XSS) vulnerabilities by:
 * 1. Using DOMPurify to sanitize the host input and remove malicious scripts
 * 2. Validating the hostname format using regex to ensure it's a valid domain
 * 3. Using URL constructor for additional validation and protocol enforcement
 * 4. Returning a safe fallback ('/') if any validation fails
 * 
 * @param host - The hostname string to validate and sanitize
 * @returns A safe HTTPS URL string or '/' as fallback
 * 
 * Example:
 * - Valid input: "example.com" → "https://example.com"
 * - Invalid input: "javascript:alert('xss')" → "/"
 * - Empty input: "" → "/"
 */
export const getSafeHostUrl = (host: string): string => {
  if (!host) return '/';
  
  // Sanitize the host to remove any potential XSS payloads
  const sanitizedHost = DOMPurify.sanitize(host, {
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [], // No attributes allowed
    KEEP_CONTENT: true // Keep text content only
  });
  
  // Additional validation for hostname format
  const hostnameRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!hostnameRegex.test(sanitizedHost)) {
    return '/';
  }
  
  // Validate it's a proper URL structure
  try {
    // Use URL constructor to validate the complete URL format
    const url = new URL(`https://${sanitizedHost}`);
    
    // Double-check protocol is HTTPS (should always be true, but extra safety)
    if (url.protocol !== 'https:') {
      return '/';
    }
    
    // Return the validated HTTPS URL
    return `https://${sanitizedHost}`;
  } catch {
    // If URL constructor throws (invalid format), return safe fallback
    return '/';
  }
};

/**
 * Returns a sanitized hostname string (without protocol prefix).
 * Useful when appending paths to the host.
 */
export const getSafeHost = (host: string): string => {
  const safeUrl = getSafeHostUrl(host);
  return safeUrl === '/' ? '' : safeUrl.replace('https://', '');
};

/**
 * Constant-time string comparison to prevent timing attacks.
 * Compares all characters regardless of mismatch position.
 */
export const safeEqual = (a: string, b: string): boolean => {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
};
